import { Injectable, computed, signal } from '@angular/core';
import { Tournament } from '../../models';
import { PlayoffService } from './playoff.service';
import { PlayoffDetail } from '../../models/playoff-detail';
import { LoggingService } from '../shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { TournamentService } from '../tournament/tournament.service';
import { CourseService } from '../course/course.service';
import { TournamentCourse } from '../../models/tournamentCourse';
import { TournamentPlayer } from '../../models/tournamentPlayer';
import { Hole } from '../../models/hole';
import { Course } from '../../models/course';
import { LeaderboardService } from '../leaderboard/leaderboard.service';
import { LeaderboardEntry } from '../../models/leaderboard-entry';

@Injectable({
  providedIn: 'root'
})
export class PlayoffStateService {
  private currentTournamentId: string | null = null;
  private currentTournament: Tournament | null = null;
  private currentPlayoffId: string | null = null;

  private readonly detailSignal = signal<PlayoffDetail | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly needsCreationSignal = signal(false);
  private readonly courseOptionsSignal = signal<PlayoffCourseOption[]>([]);
  private readonly courseHoleMapSignal = signal<Record<string, Hole[]>>({});
  private readonly availablePlayersSignal = signal<TournamentPlayer[]>([]);

  readonly detail = computed(() => this.detailSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly needsCreation = computed(() => this.needsCreationSignal());
  readonly courseOptions = computed(() => this.courseOptionsSignal());
  readonly availablePlayers = computed(() => this.availablePlayersSignal());

  constructor(
    private readonly playoffService: PlayoffService,
    private readonly loggingService: LoggingService,
    private readonly toastr: ToastrService,
    private readonly tournamentService: TournamentService,
    private readonly courseService: CourseService,
    private readonly leaderboardService: LeaderboardService
  ) { }

  initializeForTournament(tournament: Tournament | null | undefined): void {
    this.currentTournament = tournament ?? null;

    if (!tournament) {
      this.reset();
      return;
    }

    const tournamentId = tournament.tournamentId ?? null;
    if (this.currentTournamentId === tournamentId) {
      return;
    }

    this.courseOptionsSignal.set([]);
    this.courseHoleMapSignal.set({});
    this.availablePlayersSignal.set([]);

    this.currentTournamentId = tournamentId;

    const lpgaId = tournament.importSourceId;
    if (lpgaId === undefined || lpgaId === null) {
      const message = 'Tournament is missing an LPGA identifier. Playoff data cannot be loaded.';
      this.loggingService.warn('PlayoffStateService', message, { tournament });
      this.detailSignal.set(null);
      this.errorSignal.set(message);
      this.needsCreationSignal.set(false);
      return;
    }

    void this.loadTournamentContext(tournament);
    void this.loadByTournamentLpgaId(lpgaId);
  }

  private async loadByTournamentLpgaId(lpgaId: number): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.detailSignal.set(null);
    this.needsCreationSignal.set(false);

    try {
      await this.updateDetailFromFetch(lpgaId, { showMissingMessage: true, showErrorToast: true });
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async createPlayoff(): Promise<boolean> {
    if (!this.currentTournament) {
      return false;
    }

    const lpgaId = this.currentTournament.importSourceId;
    if (lpgaId === undefined || lpgaId === null) {
      const message = 'Tournament is missing an LPGA identifier. Playoff cannot be created.';
      this.loggingService.warn('PlayoffStateService', message, { tournament: this.currentTournament });
      this.errorSignal.set(message);
      return false;
    }

    const tournamentName = this.currentTournament.name ?? 'Tournament';

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const tiedLeaders = await this.getTiedLeaders(this.currentTournament);
      if (tiedLeaders !== null && tiedLeaders.length < 2) {
        const message = 'No players are currently tied for first place. Playoff cannot be created.';
        this.loggingService.warn('PlayoffStateService', message, { tournament: this.currentTournament });
        this.errorSignal.set(message);
        this.toastr.warning(message, 'Playoff');
        this.needsCreationSignal.set(true);
        return false;
      }

      await firstValueFrom(this.playoffService.createPlayoff({
        playoffId: lpgaId,
        tournamentLpgaId: lpgaId,
        tournamentName
      }));

      this.needsCreationSignal.set(false);
      this.toastr.success('Playoff created successfully.', 'Playoff');
      this.loggingService.info('PlayoffStateService', 'Playoff created successfully.', {
        tournamentLpgaId: lpgaId,
        tournamentName
      });

      await this.updateDetailFromFetch(lpgaId, { showMissingMessage: true, showErrorToast: true });
      await this.addTiedLeadersToPlayoff(tiedLeaders);
      if (tiedLeaders && tiedLeaders.length > 0) {
        await this.updateDetailFromFetch(lpgaId, { showMissingMessage: false, showErrorToast: true });
      }
      return true;
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to create playoff.', error);
      this.errorSignal.set('Unable to create playoff. Please try again.');
      this.toastr.error('Unable to create playoff.', 'Playoff');
      this.needsCreationSignal.set(true);
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private async getTiedLeaders(tournament: Tournament | null): Promise<LeaderboardEntry[] | null> {
    const tournamentId = tournament?.tournamentId;
    if (!tournamentId) {
      return null;
    }

    try {
      const leaderboard = await firstValueFrom(
        this.leaderboardService.getLeaderboard({ tournamentId })
      );

      return leaderboard.filter((row) => row.position === 1);
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to evaluate leaderboard for playoff creation.', error);
      return null;
    }
  }

  async addHoleToPlayoff(input: PlayoffHoleInput): Promise<boolean> {
    const tournament = this.currentTournament;
    const lpgaId = tournament?.importSourceId;
    const detail = this.detailSignal();
    const playoffId = detail?.playoffId ?? this.currentPlayoffId;

    if (lpgaId === undefined || lpgaId === null || !playoffId) {
      this.toastr.error('Playoff is not initialized for this tournament.', 'Playoff');
      return false;
    }

    const courseOption = this.getCourseOption(input.courseId);
    const courseName = courseOption?.courseName ?? 'Playoff Course';
    const courseShortName = courseOption?.courseShortName ?? courseName;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      let playoffCourseId = detail?.course?.playoffCourseId ?? null;

      if (!playoffCourseId) {
        playoffCourseId = await firstValueFrom(this.playoffService.createPlayoffCourse(playoffId, {
          name: courseName,
          shortName: courseShortName
        }));
      }

      const sequence = (detail?.holes?.length ?? 0) + 1;

      await firstValueFrom(this.playoffService.createPlayoffHole(playoffId, playoffCourseId, {
        sequence,
        number: input.holeNumber,
        par: input.par,
        yardage: input.yardage
      }));

      this.toastr.success('Playoff hole added.', 'Playoff');
      await this.updateDetailFromFetch(lpgaId, { showMissingMessage: false, showErrorToast: true });
      return true;
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to add playoff hole.', error);
      this.toastr.error('Unable to add playoff hole.', 'Playoff');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async addPlayersToPlayoff(players: TournamentPlayer[]): Promise<boolean> {
    if (!players.length) {
      return false;
    }

    const detail = this.detailSignal();
    const playoffId = detail?.playoffId ?? this.currentPlayoffId;
    const lpgaId = this.currentTournament?.importSourceId;

    if (!playoffId || lpgaId === undefined || lpgaId === null) {
      this.toastr.error('Playoff is not initialized for this tournament.', 'Playoff');
      return false;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      for (const player of players) {
        const lpgaPlayerId = player.lpgaId ?? 0;
        if (lpgaPlayerId === 0) {
          this.loggingService.warn('PlayoffStateService', 'Skipping tournament player without LPGA identifier.', { player });
          continue;
        }

        await firstValueFrom(this.playoffService.createPlayoffPlayer(playoffId, {
          lpgaId: lpgaPlayerId,
          wwgrId: player.wwgrId ?? 0,
          firstName: player.playerFirstName ?? '',
          lastName: player.playerLastName ?? '',
          country: player.playerCountryCode ?? player.playerCountryName ?? '',
          isAmateur: !!player.isAmateur,
          status: 'ACT'
        }));
      }

      this.toastr.success('Playoff players added.', 'Playoff');
      await this.updateDetailFromFetch(lpgaId, { showMissingMessage: true, showErrorToast: true });
      return true;
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to add playoff players.', error);
      this.toastr.error('Unable to add playoff players.', 'Playoff');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async removeLastPlayoffHole(hole: any): Promise<boolean> {
    const tournament = this.currentTournament;
    const lpgaId = tournament?.importSourceId;
    const detail = this.detailSignal();
    const playoffId = detail?.playoffId ?? this.currentPlayoffId;
    const playoffCourseId = detail?.course?.playoffCourseId;

    if (lpgaId === undefined || lpgaId === null || !playoffId || !playoffCourseId) {
      this.toastr.error('Playoff is not initialized for this tournament.', 'Playoff');
      return false;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      await firstValueFrom(
        this.playoffService.removePlayoffHole(playoffId, playoffCourseId, hole.playoffHoleId)
      );

      this.toastr.success('Playoff hole removed.', 'Playoff');
      await this.updateDetailFromFetch(lpgaId, { showMissingMessage: false, showErrorToast: true });
      return true;
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to remove playoff hole.', error);
      this.toastr.error('Unable to remove playoff hole.', 'Playoff');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  clearCreationRequest(): void {
    this.needsCreationSignal.set(false);
  }

  requestCreationPrompt(): void {
    this.needsCreationSignal.set(true);
  }

  getCourseHoles(courseId: string): Hole[] {
    return this.courseHoleMapSignal()[courseId] ?? [];
  }

  getCourseOption(courseId: string): PlayoffCourseOption | undefined {
    return this.courseOptionsSignal().find((option) => option.courseId === courseId);
  }

  reset(): void {
    this.currentTournamentId = null;
    this.currentTournament = null;
    this.currentPlayoffId = null;
    this.detailSignal.set(null);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
    this.needsCreationSignal.set(false);
    this.courseOptionsSignal.set([]);
    this.courseHoleMapSignal.set({});
    this.availablePlayersSignal.set([]);
  }

  private async loadTournamentContext(tournament: Tournament): Promise<void> {
    const tournamentId = tournament.tournamentId;
    if (!tournamentId) {
      return;
    }

    try {
      const [courses, players] = await Promise.all([
        firstValueFrom(this.tournamentService.getTournamentCoursesByTournamentId(tournamentId)),
        firstValueFrom(this.tournamentService.getTournamentPlayers(tournamentId))
      ]);

      await this.hydrateCourseOptions(courses ?? []);
      this.availablePlayersSignal.set(players ?? []);

      const detail = this.detailSignal();
      if (detail) {
        this.syncAvailablePlayers(detail);
      }
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to load tournament context for playoff.', error);
    }
  }

  private async hydrateCourseOptions(courses: TournamentCourse[]): Promise<void> {
    if (!courses.length) {
      this.courseOptionsSignal.set([]);
      this.courseHoleMapSignal.set({});
      return;
    }

    const results: Array<{ option: PlayoffCourseOption; holes: Hole[] }> = [];

    for (const tournamentCourse of courses) {
      if (!tournamentCourse.courseId) {
        continue;
      }

      try {
        const [courseDetail, holes] = await Promise.all([
          firstValueFrom(this.courseService.getById(tournamentCourse.courseId)),
          firstValueFrom(this.courseService.getHolesByCourse(tournamentCourse.courseId))
        ]);

        const normalizedCourse = this.normalizeCourseOption(tournamentCourse, courseDetail);
        results.push({ option: normalizedCourse, holes: holes ?? [] });
      } catch (error) {
        this.loggingService.error('PlayoffStateService', 'Failed to hydrate playoff course option.', {
          tournamentCourse,
          error
        });
      }
    }

    this.courseOptionsSignal.set(results.map((entry) => entry.option));

    const holeMap: Record<string, Hole[]> = {};
    for (const entry of results) {
      holeMap[entry.option.courseId] = entry.holes;
    }
    this.courseHoleMapSignal.set(holeMap);
  }

  private normalizeCourseOption(tournamentCourse: TournamentCourse, courseDetail: Course | undefined): PlayoffCourseOption {
    const courseId = tournamentCourse.courseId!;
    const courseName = courseDetail?.name ?? tournamentCourse.courseName ?? 'Course';
    const courseShortName = courseDetail?.shortName ?? courseName;

    return {
      courseId,
      courseName,
      courseShortName
    };
  }

  private async updateDetailFromFetch(lpgaId: number, options: { showMissingMessage: boolean; showErrorToast: boolean }): Promise<void> {
    try {
      const detail = await this.fetchPlayoffDetail(lpgaId);
      if (detail) {
        this.detailSignal.set(detail);
        this.errorSignal.set(null);
        this.needsCreationSignal.set(false);
        this.syncAvailablePlayers(detail);
      } else {
        await this.handleMissingPlayoffState(lpgaId, options.showMissingMessage);
      }
    } catch (error) {
      this.detailSignal.set(null);
      this.currentPlayoffId = null;
      if (options.showErrorToast) {
        this.loggingService.error('PlayoffStateService', 'Failed to load playoff detail.', error);
        this.errorSignal.set('Unable to load playoff detail. Please try again.');
        this.toastr.error('Unable to load playoff detail.', 'Playoff');
      }
    }
  }

  private async handleMissingPlayoffState(lpgaId: number, showMissingMessage: boolean): Promise<void> {
    this.detailSignal.set(null);

    if (!showMissingMessage) {
      this.needsCreationSignal.set(false);
      return;
    }

    const tournament = this.currentTournament;
    const tiedLeaders = await this.getTiedLeaders(tournament);

    if (tiedLeaders !== null && tiedLeaders.length < 2) {
      const message = 'No players are currently tied for first place. Playoff cannot be created.';
      this.loggingService.info('PlayoffStateService', message, { lpgaId });
      this.errorSignal.set(message);
      this.needsCreationSignal.set(false);
      return;
    }

    const creationMessage = 'No playoff has been created for this tournament.';
    this.loggingService.info('PlayoffStateService', creationMessage, { lpgaId });
    this.errorSignal.set(creationMessage);
    this.needsCreationSignal.set(true);
  }

  private async addTiedLeadersToPlayoff(entries: LeaderboardEntry[] | null): Promise<void> {
    if (!entries?.length) {
      return;
    }

    const detail = this.detailSignal();
    const playoffId = detail?.playoffId ?? this.currentPlayoffId;
    const tournamentId = this.currentTournament?.tournamentId;

    if (!playoffId || !tournamentId) {
      return;
    }

    await this.ensureTournamentPlayersLoaded(tournamentId);

    const availablePlayers = this.availablePlayersSignal();

    const existingLpgaIds = new Set(
      (detail?.players ?? [])
        .map((player) => player.lpgaId)
        .filter((id): id is number => id !== undefined && id !== null)
    );

    const playerLookup = this.buildTournamentPlayerLookup(availablePlayers);
    const processedIdentifiers = new Set<number>();

    for (const entry of entries) {
      const identifier = this.normalizeNumericIdentifier(entry.playerId);
      if (identifier === null || existingLpgaIds.has(identifier) || processedIdentifiers.has(identifier)) {
        continue;
      }

      const matchedPlayer = playerLookup.get(identifier);
      if (matchedPlayer) {
        await this.createPlayoffPlayerFromTournamentPlayer(playoffId, matchedPlayer, identifier);
      } else {
        await this.createPlayoffPlayerFromLeaderboardEntry(playoffId, entry, identifier);
      }

      processedIdentifiers.add(identifier);
      existingLpgaIds.add(identifier);
    }
  }

  private buildTournamentPlayerLookup(players: TournamentPlayer[]): Map<number, TournamentPlayer> {
    const lookup = new Map<number, TournamentPlayer>();

    for (const player of players) {
      const candidateIdentifiers: Array<number> = [];

      const lpgaId = this.normalizeNumericIdentifier(player.lpgaId);
      if (lpgaId !== null) {
        candidateIdentifiers.push(lpgaId);
      }

      const tournamentPlayerId = this.normalizeNumericIdentifier(player.playerId);
      if (tournamentPlayerId !== null) {
        candidateIdentifiers.push(tournamentPlayerId);
      }

      for (const identifier of candidateIdentifiers) {
        if (!lookup.has(identifier)) {
          lookup.set(identifier, player);
        }
      }
    }

    return lookup;
  }

  private normalizeNumericIdentifier(value: number | string | null | undefined): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const parsed = Number.parseInt(trimmed, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private async createPlayoffPlayerFromTournamentPlayer(
    playoffId: string,
    player: TournamentPlayer,
    fallbackLpgaId: number
  ): Promise<void> {
    try {
      const resolvedLpgaId = this.normalizeNumericIdentifier(player.lpgaId) ?? fallbackLpgaId;
      await firstValueFrom(this.playoffService.createPlayoffPlayer(playoffId, {
        lpgaId: resolvedLpgaId,
        wwgrId: player.wwgrId ?? 0,
        firstName: player.playerFirstName ?? '',
        lastName: player.playerLastName ?? '',
        country: player.playerCountryCode ?? player.playerCountryName ?? '',
        isAmateur: !!player.isAmateur,
        status: 'ACT'
      }));
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to auto assign playoff player from tournament roster.', { player, fallbackLpgaId, error });
    }
  }

  private async createPlayoffPlayerFromLeaderboardEntry(
    playoffId: string,
    entry: LeaderboardEntry,
    identifier: number
  ): Promise<void> {
    try {
      await firstValueFrom(this.playoffService.createPlayoffPlayer(playoffId, {
        lpgaId: identifier,
        wwgrId: 0,
        firstName: entry.firstName ?? '',
        lastName: entry.lastName ?? '',
        country: 'UNK',
        isAmateur: entry.isAmateur ?? false,
        status: 'ACT'
      }));
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to auto assign playoff player directly from leaderboard.', { entry, error });
    }
  }

  private async ensureTournamentPlayersLoaded(tournamentId: string): Promise<void> {
    if (this.availablePlayersSignal().length) {
      return;
    }

    try {
      const players = await firstValueFrom(this.tournamentService.getTournamentPlayers(tournamentId));
      this.availablePlayersSignal.set(players ?? []);
    } catch (error) {
      this.loggingService.error('PlayoffStateService', 'Failed to load tournament players while auto assigning playoff participants.', error);
    }
  }

  private async fetchPlayoffDetail(lpgaId: number): Promise<PlayoffDetail | null> {
    const playoff = await firstValueFrom(this.playoffService.getPlayoffByTournamentLpgaId(lpgaId));

    if (!playoff?.playoffId) {
      this.currentPlayoffId = null;
      return null;
    }

    const detail = await firstValueFrom(this.playoffService.getPlayoffDetail(playoff.playoffId));
    this.currentPlayoffId = detail.playoffId;
    return detail;
  }

  private syncAvailablePlayers(detail: PlayoffDetail): void {
    const existingLpgaIds = new Set(detail.players.map((player) => player.lpgaId).filter((id): id is number => id !== undefined && id !== null));

    this.availablePlayersSignal.update((players) =>
      players.filter((player) => {
        if (player.lpgaId === undefined || player.lpgaId === null) {
          return true;
        }
        return !existingLpgaIds.has(player.lpgaId);
      })
    );
  }
}

export interface PlayoffCourseOption {
  courseId: string;
  courseName: string;
  courseShortName: string;
}

export interface PlayoffHoleInput {
  courseId: string;
  holeNumber: number;
  par: number;
  yardage: number;
}
